import { Link } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { FormProvider } from 'react-hook-form'
import {
  Button,
  ChevronLeftIcon,
  H2,
  LoadingDots,
  Span,
  Text,
} from '@/components/atoms'
import {
  ActionModal,
  IdCard,
  InfoBanner,
  Stepper,
  StickyFooter,
} from '@/components/molecules'
import type { StepperItem } from '@/components/molecules'
import {
  CARD_FLIP_PERSPECTIVE,
  CARD_FLIP_TRANSFORM,
} from '@/shared/constants/claims'
import { ROUTES } from '@/shared/constants/routes'
import { useClaimsEditPage } from './page.claims-edit.hook'
import { ClaimsEditStepOnePage } from './step-one/page.claims-edit-step-one'
import { ClaimsEditStepTwoPage } from './step-two/page.claims-edit-step-two'

export function ClaimsEditPage() {
  const { t } = useTranslation()
  const page = useClaimsEditPage()

  if (page.isLoading) {
    return (
      <div className="flex min-h-full items-center justify-center bg-white">
        <LoadingDots className="loading-xl" />
      </div>
    )
  }

  if (page.isNotFound || page.isFiled) {
    return (
      <div className="mx-auto flex min-h-[calc(100vh-4rem)] w-full max-w-3xl flex-col gap-6 p-6 pt-8">
        <Link
          to={ROUTES.CLAIM_DETAIL}
          params={{ claimId: page.claimId }}
          className="flex items-center gap-1 text-sm text-neutral-600"
        >
          <ChevronLeftIcon />
          {t('claims.edit.back')}
        </Link>
        {page.isFiled ? (
          <InfoBanner message={t('claims.edit.errors.alreadyFiled')} />
        ) : (
          <Text className="py-16 text-center text-neutral-600">
            {t('claims.detail.notFound')}
          </Text>
        )}
      </div>
    )
  }

  const steps: StepperItem[] = [
    {
      label: t('claims.pet.stepOne.label'),
      children: <ClaimsEditStepOnePage page={page} />,
    },
    {
      label: t('claims.pet.stepTwo.label'),
      children: <ClaimsEditStepTwoPage page={page} />,
    },
  ]

  return (
    <FormProvider {...page.formMethods}>
      <div className="flex min-h-dvh flex-col bg-white">
        <div className="mx-auto w-full max-w-266.5 px-6 pt-6 md:px-0">
          <Link
            to={ROUTES.CLAIMS}
            className="text-indigo-base flex w-fit items-center gap-1 text-sm"
            data-testid="claim-form-back-to-claims"
            aria-label={t('claims.pet.back')}
          >
            <ChevronLeftIcon />
          </Link>
        </div>
        {/* overflow-clip (not hidden): a hidden container is still scrollable, and
          focusing an input made the browser scroll it towards the off-screen step */}
        <div className="mx-auto flex w-full max-w-266.5 flex-1 flex-col overflow-clip px-6 pt-6 pb-0 md:flex-row md:gap-8 md:px-0 md:pt-8 md:pb-32">
          <div className="z-10 hidden flex-col bg-white md:flex md:max-w-117 md:justify-start">
            {/* Grid rows 0fr → 1fr animates the height, so the title below slides down smoothly */}
            <div
              className={`grid grid-rows-[1fr] transition-[grid-template-rows] duration-500 ease-out`}
            >
              <div className="min-h-0 overflow-hidden">
                <div
                  className={`translate-y-0 pb-6 opacity-100 transition duration-500 ease-out`}
                >
                  <div
                    className="transition-transform duration-300"
                    style={{
                      perspective: CARD_FLIP_PERSPECTIVE,
                      transformStyle: 'preserve-3d',
                      transform: page.isFlipping
                        ? CARD_FLIP_TRANSFORM.FLIPPING
                        : CARD_FLIP_TRANSFORM.IDLE,
                    }}
                  >
                    {page.currentStep === 0 ? (
                      <IdCard
                        variant="with-photo"
                        photoUrl={page.card.photoUrl}
                        hidePhotoBackground={page.card.hidePhotoBackground}
                        planName={page.card.petName}
                        ownerName={page.card.ownerName}
                        policyId={page.card.policyId}
                        startDate={page.card.startDate}
                        className="hidden max-w-83.5 md:flex"
                      />
                    ) : (
                      <IdCard
                        variant="no-photo"
                        policyId={page.card.policyId}
                        birthday={page.card.birthday}
                        weight={page.card.weightLabel}
                        vetName={page.card.vetName}
                        className="hidden max-w-83.5 md:flex"
                      />
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="hidden flex-col gap-6 md:flex">
              <H2 className="text-[3.125rem]! leading-15!">
                <Span className="text-neutral-900" textSize="inherit">
                  {t('claims.edit.titleStart')}
                </Span>
                <br />
                <Span className="text-indigo-base" textSize="inherit">
                  {t('claims.edit.titleHighlight')}
                </Span>
              </H2>
              <Text className="text-neutral-900">
                {t('claims.edit.description')}
              </Text>
            </div>
          </div>
          <div className="flex flex-1 flex-col">
            <Stepper
              items={steps}
              currentStep={page.currentStep}
              dynamicHeight
              className="md:gap-8"
            />
          </div>
        </div>

        <StickyFooter>
          {page.isLastStep ? (
            <>
              <Button
                variant="secondary"
                onClick={page.handleBack}
                className="h-12 w-1/2 px-2! md:w-auto md:px-8!"
                data-testid="claim-edit-back"
              >
                {t('claims.pet.back')}
              </Button>
              <Button
                onClick={page.handleNext}
                loading={page.isSaving}
                disabled={page.isSaving || page.isOffline}
                className="h-12 w-1/2 md:w-auto"
                data-testid="claim-edit-save"
              >
                {t('claims.edit.save')}
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="secondary"
                onClick={page.handleCancel}
                className="h-12 w-1/2 px-2! md:w-auto md:px-8!"
                data-testid="claim-edit-cancel"
              >
                {t('claims.edit.cancel')}
              </Button>
              <Button
                onClick={page.handleNext}
                className="h-12 w-1/2 md:w-auto"
                data-testid="claim-edit-continue"
              >
                {t('claims.pet.continue')}
              </Button>
            </>
          )}
        </StickyFooter>
      </div>

      <ActionModal
        isOpen={page.submitModal.isOpen}
        variant={page.submitModal.variant}
        title={page.submitModal.title}
        description={page.submitModal.description}
        primaryAction={page.submitModal.primaryAction}
        secondaryAction={page.submitModal.secondaryAction}
        onClose={page.submitModal.onClose}
      >
        {page.submitModal.errorMessage && (
          <Text className="text-error-base text-sm!">
            {page.submitModal.errorMessage}
          </Text>
        )}
      </ActionModal>
    </FormProvider>
  )
}
